import type { AuthorizedResourceAccessMap } from '@/permissions/adapter';
import type { BusinessType } from '@/platform/api';
import { canUseMasterData } from '@/platform/masterData';
/** Backend grants are computed across the existing business namespaces. */
export default function access(initialState?: {
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  hasRegisteredAiScenes?: boolean;
  enabledBusinessTypes?: BusinessType[];
}) {
  const grants = initialState?.authorizedResourceAccess ?? {};
  const admin = Boolean(initialState?.currentTenantIsAdmin);
  const types = initialState?.enabledBusinessTypes ?? [];
  const any = (...keys: string[]) => keys.some((key) => grants[`${key}:view`]);
  const enabled = types.length > 0;
  return {
    ...grants,
    tenantAdmin: admin,
    /**
     * 设置页与退出域配置的入口门禁。原先路由上硬写 tenantAdmin，
     * 让 investment-settings / exit-settings 两条资源在前端完全不起作用；
     * 这里改成取或：租户管理员 ∨ 对应资源的 view。
     * 页面内的写操作仍各自按 :manage 判（见 pages/Dictionaries.tsx 等三处既有写法）。
     */
    settingsView: admin || Boolean(grants['investment-settings:view']),
    exitSettingsView: admin || Boolean(grants['exit-settings:view']),
    /**
     * 工作台分组：6 个页面各自挂 workbench-*:view，分组本身取任一，
     * 与 fundModule / investmentModule 等同形。
     * 不按 enabled 收口：日程、待办、关注、动态是跨业务域的入口，
     * 与 tenantAdmin 一样不属于「某个业务域是否启用」的判断面。
     */
    workbenchModule: any(
      'workbench-overview',
      'workbench-schedule',
      'workbench-tasks',
      'workbench-focus',
      'workbench-activity',
      // 风险与履约子项按后端现状走 investment-settings；
      // 分组取任一要覆盖子项 access 键的并集，否则该子项会被分组挡成菜单不可达。
      'investment-settings',
    ),
    masterDataView:
      enabled && canUseMasterData({ ...grants, tenantAdmin: admin }, 'view'),
    modelSettings: Boolean(admin && initialState?.hasRegisteredAiScenes),
    analysisModule:
      enabled &&
      any(
        'analysis-overview',
        'investment-dashboard',
        'investment-projects',
        'investment-fund-operations',
        // /analysis/portfolio 这一子项按 portfolio-company:view 判，分组要跟着覆盖。
        'portfolio-company',
        'portfolio-report',
        'exit-dashboard',
        'research-topics',
      ),
    fundModule:
      enabled &&
      any(
        'investment-fundraising',
        'investment-fund-setup',
        'investment-fund-operations',
        'fund-cash-call',
        'fund-payment',
        'fund-distribution',
      ),
    fundOperationsWorkspace:
      enabled &&
      any(
        'investment-fund-operations',
        'fund-cash-call',
        'fund-payment',
        'fund-distribution',
      ),
    fundDisclosureWorkspace:
      enabled && any('investment-fund-setup', 'investment-fund-operations'),
    fundDistributionWorkspace:
      enabled && any('fund-distribution', 'investment-fund-operations'),
    fundRecordModule:
      enabled &&
      any(
        'investment-fund-setup',
        'investment-fund-operations',
        'fund-cash-call',
        'fund-payment',
        'fund-distribution',
        'investment-fundraising',
      ),
    // 分组的 any-of 必须覆盖子菜单项 access 键的并集（routes.test.ts 有机器判据），
    // 否则只拿到概览或台账权限的人，子项亮着、分组挡着，菜单永远点不进去。
    investmentModule:
      enabled &&
      any(
        'investment-overview',
        'investment-leads',
        'investment-projects',
        'investment-decisions',
        'investment-transactions',
        'investment-relationships',
        'investment-dashboard',
      ),
    portfolioModule:
      enabled &&
      any(
        'portfolio-company',
        'portfolio-report',
        'exit-project',
        'exit-opportunity',
        'exit-transaction',
      ),
    exitModule:
      enabled && any('exit-project', 'exit-opportunity', 'exit-transaction'),
    researchModule:
      enabled &&
      any(
        'research-topics',
        'research-companies',
        'research-materials',
        'research-viewpoints',
        'research-study-topics',
      ),
  };
}
